/**
 * KOS Search Overlay Web Component
 * File: assets/component-search-overlay.js
 */

(function () {
  if (customElements.get('search-overlay')) return;

  class SearchOverlay extends HTMLElement {
    constructor() {
      super();
      this.previousActiveElement = null;
      this.debounceTimer = null;
      this.abortController = null;
      this.lastQuery = '';
    }

    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('[data-search-form]');
      this.resultsEl = this.querySelector('[data-search-results]');
      this.loadingEl = this.querySelector('[data-search-loading]');
      this.emptyEl = this.querySelector('[data-search-empty]');
      this.statusEl = this.querySelector('[data-search-status]');

      this.bindEvents();
    }

    bindEvents() {
      // Close buttons
      this.querySelectorAll('[data-search-close]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });

      // Global open event
      document.addEventListener('search:open', () => this.open());

      // Toggle buttons (header search icon, etc.)
      document.querySelectorAll('[data-search-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Keyboard
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        }
      });

      // Input events
      if (this.input) {
        this.input.addEventListener('input', () => {
          this.onInput();
        });
      }

      // Form submit
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          const q = this.input.value.trim();
          if (!q) {
            e.preventDefault();
            this.input.focus();
          }
          // Otherwise let the form submit naturally to /search?q=...
        });
      }
    }

    open() {
      this.previousActiveElement = document.activeElement;
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      // iOS Safari requires .focus() to be called synchronously within the
      // originating user gesture, otherwise the software keyboard will not open.
      // Any indirection (requestAnimationFrame, setTimeout, await, transitionend)
      // breaks that chain. We reveal the overlay via `.is-open` above, then
      // focus immediately — reading offsetWidth first to force a synchronous
      // style/layout flush so the (previously display:none) input is focusable.
      if (this.input) {
        // Force layout so the new display value takes effect before focus.
        // eslint-disable-next-line no-unused-expressions
        this.input.offsetWidth;
        this.input.focus({ preventScroll: true });
        this.input.select();
      }
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';

      // Cancel pending requests
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      if (this.previousActiveElement) {
        this.previousActiveElement.focus();
        this.previousActiveElement = null;
      }
    }

    onInput() {
      const query = this.input.value.trim();

      // Clear previous debounce
      clearTimeout(this.debounceTimer);

      if (query.length === 0) {
        this.showState('idle');
        this.lastQuery = '';
        return;
      }

      if (query === this.lastQuery) return;

      this.debounceTimer = setTimeout(() => {
        this.fetchPredictive(query);
      }, 300);
    }

    async fetchPredictive(query) {
      // Abort any in-flight request
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.showState('loading');

      const url = `${Theme.routes.predictive_search_url}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6`;

      try {
        const res = await fetch(url, {
          signal: this.abortController.signal
        });
        if (!res.ok) throw new Error(res.statusText);

        const data = await res.json();
        this.lastQuery = query;
        this.renderResults(data.resources.results, query);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Search error:', err);
        this.showState('empty', query);
      }
    }

    renderResults(results, query) {
      const products = (results.products || []).filter(p => !(p.tags || []).includes('HIDDEN_PRODUCT'));
      const collections = results.collections || [];

      const total = products.length + collections.length;

      if (total === 0) {
        this.showState('empty', query);
        return;
      }

      let html = '';

      // Products
      if (products.length > 0) {
        html += `<div class="search-overlay__group">`;
        html += `<h3 class="search-overlay__group-title">Products</h3>`;
        html += `<div class="search-overlay__group-list">`;
        for (const product of products) {
          const image = product.image
            ? `<img class="search-result__image" src="${this.resizeImage(product.image, '96x96')}" alt="" width="48" height="48" loading="lazy">`
            : '';

          let priceHtml = '';
          const comparePrice = parseFloat(product.compare_at_price_max) || 0;
          const currentPrice = parseFloat(product.price) || 0;
          if (comparePrice > 0 && comparePrice > currentPrice) {
            priceHtml = `<span class="search-result__price--sale">${this.formatMoney(product.price)}</span><span class="search-result__price--compare">${this.formatMoney(product.compare_at_price_max)}</span>`;
          } else {
            priceHtml = this.formatMoney(product.price);
          }

          html += `
            <a href="${product.url}" class="search-result">
              ${image}
              <div class="search-result__info">
                <p class="search-result__title">${this.highlightMatch(product.title, query)}</p>
                <p class="search-result__meta">${priceHtml}</p>
              </div>
            </a>`;
        }
        html += `</div></div>`;
      }

      // Collections
      if (collections.length > 0) {
        html += `<div class="search-overlay__group">`;
        html += `<h3 class="search-overlay__group-title">Collections</h3>`;
        html += `<div class="search-overlay__group-list">`;
        for (const col of collections) {
          html += `
            <a href="${col.url}" class="search-result search-result--text">
              <div class="search-result__info">
                <p class="search-result__title">${this.highlightMatch(col.title, query)}</p>
              </div>
            </a>`;
        }
        html += `</div></div>`;
      }

      // View all results link
      const searchUrl = `${Theme.routes.search_url}?q=${encodeURIComponent(query)}&type=product,collection`;
      html += `
        <a href="${searchUrl}" class="search-overlay__view-all">
          View all results
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M5 3l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>`;

      this.resultsEl.innerHTML = html;
      this.showState('results');

      // Announce results count
      if (this.statusEl) {
        this.statusEl.textContent = `${total} result${total === 1 ? '' : 's'} found`;
      }
    }

    showState(state, query) {
      const loading = this.loadingEl;
      const results = this.resultsEl;
      const empty = this.emptyEl;

      loading.hidden = state !== 'loading';
      results.hidden = state !== 'results';
      empty.hidden = state !== 'empty';

      if (state === 'idle') {
        results.innerHTML = '';
      }

      if (state === 'empty' && query) {
        const title = empty.querySelector('.search-overlay__empty-title');
        if (title) {
          title.textContent = `No results found for "${query}"`;
        }
      }

      if (state === 'empty' && this.statusEl) {
        this.statusEl.textContent = 'No results found';
      }
    }

    resizeImage(url, size) {
      if (!url) return '';
      // Shopify CDN image resizing
      if (url.includes('.jpg') || url.includes('.png') || url.includes('.webp') || url.includes('.gif')) {
        return url.replace(/\.(jpg|png|webp|gif)/, `_${size}.$1`);
      }
      return url;
    }

    formatMoney(value) {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(num)) return value;
      // Predictive search API returns dollar amounts (not cents)
      const amount = num.toFixed(2);
      const amountNoDec = Math.round(num);
      const format = (typeof Theme !== 'undefined' && Theme.moneyFormat) || '${{amount}}';
      return format
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(amountNoDec));
    }

    highlightMatch(text, query) {
      const escaped = this.escapeHtml(text);
      const escapedQuery = this.escapeHtml(query);
      const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return escaped.replace(regex, '<mark class="search-result__highlight">$1</mark>');
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('search-overlay', SearchOverlay);
})();
